// Per Ankh — motion for the site header, the dropdowns and the drawer (GSAP).
//
// Everything here is presentation: the header works the same with none of it, and
// with "reduce motion" asked for at the system level every animation becomes an
// instant change rather than being skipped, so menus still open and close.

import { gsap } from 'gsap';

const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Seconds, or 0 when the visitor asked for less motion. */
const dur = (s) => (reduced() ? 0 : s);

/**
 * The hover pill: one soft gold lozenge behind the nav that glides to whatever the
 * pointer is over, and rests on the current page's link when it leaves.
 *
 * @param {HTMLElement} nav  the `.site-header__nav` element; rebuilt links are fine,
 *                           because targets are looked up on each event
 */
export function attachNavPill(nav) {
  let pill = nav.querySelector('.site-header__pill');
  if (!pill) {
    pill = document.createElement('span');
    pill.className = 'site-header__pill';
    pill.setAttribute('aria-hidden', 'true');
    nav.prepend(pill);
  }
  gsap.set(pill, { autoAlpha: 0 });

  const targets = () => [...nav.querySelectorAll('.site-header__link, .site-header__menu-trigger')];
  const moveTo = (el, instant = false) => {
    if (!el) {
      gsap.to(pill, { autoAlpha: 0, duration: dur(0.2) });
      return;
    }
    const navBox = nav.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    gsap.to(pill, {
      x: box.left - navBox.left,
      width: box.width,
      height: box.height,
      y: box.top - navBox.top,
      autoAlpha: 1,
      duration: instant ? 0 : dur(0.38),
      ease: 'power3.out',
    });
  };
  const rest = () => moveTo(nav.querySelector('.site-header__link.is-active'));

  nav.addEventListener('pointerover', (e) => {
    const hit = targets().find((t) => t.contains(e.target));
    if (hit) moveTo(hit);
  });
  nav.addEventListener('pointerleave', rest);
  nav.addEventListener('focusin', (e) => {
    const hit = targets().find((t) => t.contains(e.target));
    if (hit) moveTo(hit);
  });
  window.addEventListener('resize', () => rest());
  // First placement without a glide, once fonts have settled the link widths.
  requestAnimationFrame(() => {
    const active = nav.querySelector('.site-header__link.is-active');
    if (active) moveTo(active, true);
  });
  return { rest };
}

/**
 * Open a dropdown panel: it drops and fades in, and its items follow one by one.
 * @param {HTMLElement} panel
 */
export function openPanel(panel) {
  gsap.killTweensOf(panel);
  panel.hidden = false;
  const items = panel.querySelectorAll('.site-header__menu-link');
  gsap.killTweensOf(items);
  gsap.fromTo(
    panel,
    { autoAlpha: 0, y: -8, scale: 0.97, transformOrigin: 'top center' },
    { autoAlpha: 1, y: 0, scale: 1, duration: dur(0.28), ease: 'power3.out' },
  );
  gsap.fromTo(
    items,
    { autoAlpha: 0, x: -10 },
    { autoAlpha: 1, x: 0, duration: dur(0.32), ease: 'power2.out', stagger: dur(0.035), delay: dur(0.05) },
  );
}

/**
 * Close a dropdown panel, hiding it for real once the fade ends so it leaves the
 * tab order and the accessibility tree.
 * @param {HTMLElement} panel
 */
export function closePanel(panel) {
  if (panel.hidden) return;
  gsap.killTweensOf(panel);
  gsap.to(panel, {
    autoAlpha: 0,
    y: -6,
    duration: dur(0.18),
    ease: 'power2.in',
    onComplete: () => {
      panel.hidden = true;
      gsap.set(panel, { clearProps: 'opacity,visibility,transform' });
    },
  });
}

/**
 * The header on scroll: it slims down once the page moves, slides away while the
 * reader scrolls down into content, and comes straight back on any scroll up.
 * Never hides while one of its menus is open, or near the top of the page.
 *
 * @param {HTMLElement} header
 * @param {() => boolean} menuOpen
 */
export function attachScrollBehaviour(header, menuOpen) {
  let lastY = window.scrollY;
  let hidden = false;
  let ticking = false;

  const show = (yes) => {
    if (yes === !hidden) return;
    hidden = !yes;
    gsap.to(header, { yPercent: yes ? 0 : -105, duration: dur(yes ? 0.35 : 0.45), ease: yes ? 'power3.out' : 'power2.in' });
  };

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    header.classList.toggle('is-compact', y > 24);
    const delta = y - lastY;
    if (y < 120 || menuOpen()) show(true);
    else if (delta > 6) show(false);
    else if (delta < -6) show(true);
    lastY = y;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  // Keyboard users tabbing into a hidden header get it back.
  header.addEventListener('focusin', () => show(true));
  update();
}

/**
 * Cascade the drawer's links in each time it opens. Wraps the drawer's own `open`,
 * so the drawer keeps doing everything else it already does.
 */
export function attachDrawerMotion() {
  const wrap = () => {
    const api = window.perAnkhSideNav;
    if (!api || api.__motion) return Boolean(api);
    const open = api.open;
    api.open = (...args) => {
      const result = open.apply(api, args);
      const links = document.querySelectorAll('.pa-side-nav .pa-side-link, .pa-side-nav .pa-side-section-title');
      // The drawer's CSS transitions would chase every frame GSAP writes, so they are
      // off for the cascade and handed back (with every inline style) at its end.
      gsap.fromTo(
        links,
        { autoAlpha: 0, x: -18, transition: 'none' },
        {
          autoAlpha: 1, x: 0, duration: dur(0.4), ease: 'power3.out', stagger: dur(0.025), delay: dur(0.08), overwrite: true,
          onComplete: () => gsap.set(links, { clearProps: 'opacity,visibility,transform,transition' }),
        },
      );
      return result;
    };
    api.__motion = true;
    return true;
  };
  // The drawer script may finish after this one.
  if (!wrap()) {
    let tries = 0;
    const timer = setInterval(() => {
      if (wrap() || ++tries > 40) clearInterval(timer);
    }, 100);
  }
}
